import React, { useState, useMemo, useEffect } from 'react';
import {
  ExclamationTriangleIcon,
  FireIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { RatioData } from './data/projectsData';
import {
  computeNodeStats,
  computeAllChildrenStats,
  hasChildrenAtPath,
  LotDistribution,
} from './data/ratioStats';
import RatioRangeBar from './Ratio_RangeBar';
import Tooltip from './Ratio_Tooltip';

interface AuditLotProps {
  allProjects: RatioData[];
}

const fmt  = (v: number) => v.toLocaleString('fr-FR');
const fmtK = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M€`;
  if (Math.abs(v) >= 1_000)     return `${Math.round(v / 1_000).toLocaleString('fr-FR')} k€`;
  return `${Math.round(v).toLocaleString('fr-FR')} €`;
};

// ─── KPI Card ────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string;
  sub: string;
  color: 'gray' | 'red' | 'green' | 'violet';
  tooltip?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, sub, color, tooltip }) => {
  const valueClass: Record<string, string> = {
    gray:   'text-gray-800',
    red:    'text-red-600',
    green:  'text-green-600',
    violet: 'text-violet-600',
  };

  const inner = (
    <div className="px-4 py-4">
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      <div className={`text-xl font-bold ${valueClass[color]}`}>{value}</div>
      <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip text={tooltip} position="top" width="w-64" as="div" className="cursor-help">
        {inner}
      </Tooltip>
    );
  }
  return inner;
};

// ─── Barre de dispersion (récursive) ─────────────────────────────────────────

interface DispersionRowProps {
  name: string;
  dist: LotDistribution;
  avgSurface: number;
  projects: RatioData[];
  path: string[];
  depth?: number;
}

const DispersionRow: React.FC<DispersionRowProps> = ({ name, dist, avgSurface, projects, path, depth = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pathKey = path.join('\x00');

  const canExpand = useMemo(
    () => hasChildrenAtPath(projects, path),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, pathKey]
  );

  const childrenArray = useMemo(() => {
    if (!isExpanded) return [];
    return Array.from(computeAllChildrenStats(projects, path).entries());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, projects, pathKey]);

  const spreadPct = dist.median > 0
    ? Math.round((dist.p75 - dist.p25) / dist.median * 100)
    : 0;
  const isAlert = spreadPct > 20 || dist.cv > 0.3;
  const paddingLeft = 20 + depth * 20;

  return (
    <div className="border-b border-gray-50 last:border-0">
      <div style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '20px', paddingTop: '12px', paddingBottom: '12px' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {isAlert && <ExclamationTriangleIcon className="h-3.5 w-3.5 text-red-400 shrink-0" />}
            <span className={`text-xs font-medium ${depth === 0 ? 'text-gray-700' : 'text-gray-600'}`}>{name}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
              isAlert ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'
            }`}>±{spreadPct}%</span>
            <span className="text-xs text-gray-400">{dist.nbProjets} proj.</span>
          </div>
        </div>
        <RatioRangeBar dist={dist} surface={avgSurface} compact />
        {canExpand && (
          <button
            onClick={() => setIsExpanded(v => !v)}
            className="mt-1.5 text-xs text-gray-400 hover:text-violet-600 transition-colors"
          >
            {isExpanded ? '↑ cacher' : '↳ voir les sous-ensembles'}
          </button>
        )}
      </div>
      {isExpanded && childrenArray.map(([childName, childDist]) => (
        <DispersionRow
          key={childName}
          name={childName}
          dist={childDist}
          avgSurface={avgSurface}
          projects={projects}
          path={[...path, childName]}
          depth={depth + 1}
        />
      ))}
    </div>
  );
};

// ─── Collecte récursive de tous les descendants d'un nœud ───────────────────

function getAllDescendants(
  projects: RatioData[],
  path: string[]
): Array<{ name: string; dist: LotDistribution; parentPath: string[]; isLeaf: boolean }> {
  const result: Array<{ name: string; dist: LotDistribution; parentPath: string[]; isLeaf: boolean }> = [];
  computeAllChildrenStats(projects, path).forEach((dist, childName) => {
    const childPath = [...path, childName];
    const hasChildren = hasChildrenAtPath(projects, childPath);
    result.push({ name: childName, dist, parentPath: path, isLeaf: !hasChildren });
    if (hasChildren) {
      result.push(...getAllDescendants(projects, childPath));
    }
  });
  return result;
}

// ─── Composant principal ──────────────────────────────────────────────────────

const AuditLot: React.FC<AuditLotProps> = ({ allProjects }) => {
  const lotNames = useMemo(() => {
    const names = new Set<string>();
    allProjects.forEach(p => p.lots.forEach(l => names.add(l.nom)));
    return Array.from(names).sort();
  }, [allProjects]);

  const [selectedLot, setSelectedLot] = useState(
    lotNames.find(n => /cvc|plomberie|go|fondation/i.test(n)) ?? lotNames[0] ?? ''
  );
  const [showRankings, setShowRankings] = useState(true);
  const [excludedProjectIds, setExcludedProjectIds] = useState<Set<string>>(new Set());
  const [resolvedPostes, setResolvedPostes] = useState<Set<string>>(new Set());
  const [showResolved, setShowResolved] = useState(false);

  // Réinitialise les exclusions et les postes résolus quand on change de lot
  useEffect(() => {
    setExcludedProjectIds(new Set());
    setResolvedPostes(new Set());
  }, [selectedLot]);

  // Projets retenus pour les calculs (sans les exclus)
  const filteredProjects = useMemo(
    () => excludedProjectIds.size === 0
      ? allProjects
      : allProjects.filter(p => !excludedProjectIds.has(p.id)),
    [allProjects, excludedProjectIds]
  );

  // Stats calculées uniquement sur les projets retenus
  const lotDist = useMemo(
    () => computeNodeStats(filteredProjects, [selectedLot]),
    [filteredProjects, selectedLot]
  );

  const childrenStats = useMemo(
    () => computeAllChildrenStats(filteredProjects, [selectedLot]),
    [filteredProjects, selectedLot]
  );
  const childrenArray = useMemo(
    () => Array.from(childrenStats.entries()),
    [childrenStats]
  );

  // Classement : tous les projets affichés, mais les exclus sont marqués
  const projectRankings = useMemo(() => {
    const median = lotDist?.median ?? 0;
    return allProjects
      .map(p => {
        const lot = p.lots.find(l => l.nom === selectedLot);
        if (!lot) return null;
        return {
          id:          p.id,
          projet:      p.projet,
          ratioM2:     lot.ratioM2,
          cout:        lot.cout,
          surface:     p.surface,
          excluded:    excludedProjectIds.has(p.id),
          deviancePct: median > 0
            ? Math.round((lot.ratioM2 - median) / median * 100)
            : 0,
        };
      })
      .filter((r): r is NonNullable<typeof r> => r !== null)
      .sort((a, b) => Math.abs(b.deviancePct) - Math.abs(a.deviancePct));
  }, [allProjects, selectedLot, lotDist, excludedProjectIds]);

  const includedRankings = useMemo(
    () => projectRankings.filter(r => !r.excluded),
    [projectRankings]
  );

  const n = includedRankings.length;
  const nExcluded = excludedProjectIds.size;

  const avgSurface = useMemo(() => {
    if (n === 0) return 1000;
    return Math.round(includedRankings.reduce((s, r) => s + r.surface, 0) / n);
  }, [includedRankings, n]);

  // Tous les descendants pour les anomalies — avec flag isLeaf pour les calculs sans double-comptage
  const allDescendants = useMemo(
    () => getAllDescendants(filteredProjects, [selectedLot]),
    [filteredProjects, selectedLot]
  );

  // Facteur réaliste : on n'atteint jamais P25 sur tous les postes simultanément.
  // 50% du potentiel théorique (médiane → P25) correspond à cibler P37,5 implicitement —
  // la médiane des entreprises en dessous de la médiane de marché.
  // Standard dans les modèles de strategic sourcing : une mise en concurrence bien préparée
  // permet d'aller chercher la moitié de l'écart observable.
  const REALISTIC_FACTOR = 0.5;

  // Économie CV-pondérée : facteur adapté lot par lot selon la dispersion réelle
  // factor = max(0.25, min(0.65, 1 − cv))
  // CV faible → lot homogène, P25 fiable → facteur élevé
  // CV élevé  → lot dispersé, P25 ambitieux → facteur réduit
  const totalEconomieCvWeighted = useMemo(() =>
    Math.round(
      allDescendants
        .filter(d => d.isLeaf)
        .reduce((sum, { dist: d }) => {
          const factor = Math.max(0.25, Math.min(0.65, 1 - d.cv));
          return sum + Math.max(0, d.median - d.p25) * avgSurface * factor;
        }, 0)
    ),
    [allDescendants, avgSurface]
  );

  const avgCv = useMemo(() => {
    const leaves = allDescendants.filter(d => d.isLeaf);
    if (leaves.length === 0) return 0;
    return leaves.reduce((s, { dist: d }) => s + d.cv, 0) / leaves.length;
  }, [allDescendants]);

  // Toutes les anomalies triées — chaque poste avec sa clé stable, son économie, son enjeu
  const allAnomalies = useMemo(() =>
    allDescendants
      .map(({ name, dist: d, parentPath }) => ({
        key:                `${parentPath.slice(1).join('__')}__${name}`,
        name,
        parentLabel:        parentPath.length > 1 ? parentPath.slice(1).join(' › ') : null,
        ecartPct:           d.median > 0 ? Math.round((d.p75 - d.p25) / d.median * 100) : 0,
        enjeuEuros:         Math.round((d.p75 - d.p25) * avgSurface),
        economiePotentielle: Math.round(Math.max(0, d.median - d.p25) * avgSurface * REALISTIC_FACTOR),
        nbProjets:          d.nbProjets,
      }))
      .sort((a, b) => b.enjeuEuros - a.enjeuEuros),
    [allDescendants, avgSurface]
  );

  const activeAnomalies  = useMemo(() => allAnomalies.filter(a => !resolvedPostes.has(a.key)).slice(0, 5), [allAnomalies, resolvedPostes]);
  const resolvedAnomalies = useMemo(() => allAnomalies.filter(a =>  resolvedPostes.has(a.key)),            [allAnomalies, resolvedPostes]);

  const handleToggleResolved = (key: string) => {
    setResolvedPostes(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });
  };

  // KPIs (tous sur projets retenus)
  const ecartMaxEuros  = lotDist ? Math.round((lotDist.max - lotDist.min) * avgSurface) : 0;
  const economieEuros  = lotDist ? Math.round((lotDist.median - lotDist.p25) * avgSurface) : 0;
  const negoPct        = lotDist && lotDist.median > 0
    ? Math.round((lotDist.median - lotDist.p25) / lotDist.median * 100)
    : 0;
  const totalAnalyse   = includedRankings.reduce((s, r) => s + r.cout, 0);

  const handleToggleProject = (id: string) => {
    setExcludedProjectIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  if (!lotDist && n === 0) {
    return (
      <div className="p-8 text-center text-gray-400 italic text-sm">
        Aucune donnée disponible pour ce lot.
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Dashboard — sélecteur de lot ──────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Lot analysé</label>
            <div className="relative">
              <select
                value={selectedLot}
                onChange={e => setSelectedLot(e.target.value)}
                className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-800 pr-8 focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                {lotNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="flex items-center gap-3 pb-0.5">
            <div className="text-center">
              <div className="text-lg font-bold text-violet-600">{n}</div>
              <div className="text-xs text-gray-400">projet{n !== 1 ? 's' : ''} retenus</div>
            </div>
            {nExcluded > 0 && (
              <div className="text-center">
                <div className="text-lg font-bold text-amber-500">{nExcluded}</div>
                <div className="text-xs text-gray-400">exclu{nExcluded > 1 ? 's' : ''}</div>
              </div>
            )}
            <div className="text-center">
              <div className="text-lg font-bold text-gray-700">{fmt(avgSurface)}</div>
              <div className="text-xs text-gray-400">m² moyen</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-700">{childrenArray.length}</div>
              <div className="text-xs text-gray-400">sous-poste{childrenArray.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>

        {/* Économie de négociation */}
        {totalEconomieCvWeighted > 0 && (
          <Tooltip
            text={`Estimation ajustée selon la fiabilité du benchmarking de chaque poste.\n\nPour chaque poste, le facteur d'atteinte est calculé à partir du coefficient de variation (CV = σ/μ) :\n  factor = max(25% · min(65% · 1 − CV))\n\nCV faible → poste homogène, P25 fiable → facteur élevé\nCV élevé  → poste dispersé, P25 ambitieux → facteur réduit\n\nCV moyen sur ce lot : ${(avgCv * 100).toFixed(0)} % → facteur moyen ${(Math.max(0.25, Math.min(0.65, 1 - avgCv)) * 100).toFixed(0)} %\nPotentiel théorique brut (médiane → P25) : ${fmtK(allDescendants.filter(d => d.isLeaf).reduce((s, { dist: d }) => s + Math.max(0, d.median - d.p25) * avgSurface, 0))}\n\nBase : ${fmt(avgSurface)} m² · ${n} projets comparables retenus`}
            position="bottom"
            width="w-96"
            as="div"
          >
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3 cursor-help">
              <div>
                <p className="text-xs font-semibold text-green-800">Économie de négociation estimée</p>
                <p className="text-xs text-green-600 mt-0.5">
                  Ajustée selon la fiabilité du benchmarking de chaque poste
                </p>
              </div>
              <div className="text-right shrink-0 ml-4">
                <span className="text-2xl font-bold text-green-700">{fmtK(totalEconomieCvWeighted)}</span>
                {totalAnalyse > 0 && (
                  <div className="text-xs font-semibold text-green-600 mt-0.5">
                    ↓ {(totalEconomieCvWeighted / totalAnalyse * 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} % du total lot
                  </div>
                )}
              </div>
            </div>
          </Tooltip>
        )}
      </div>

      {/* ── Section 1 : Le choc ─────────────────────────────────────────────── */}
      {lotDist && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="px-5 pt-5 pb-4 border-b border-gray-100">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  Sur ce lot, l'écart entre le prix le plus bas et le plus haut représente{' '}
                  <span className="text-red-600 font-bold">{fmtK(ecartMaxEuros)}</span>{' '}
                  pour une opération de {fmt(avgSurface)} m².
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  En visant le 1<sup>er</sup> quartile plutôt que la médiane, l'économie atteignable est de{' '}
                  <span className="font-semibold text-green-700">{fmtK(economieEuros)} (↓ {negoPct}%)</span>.
                  {nExcluded > 0 && (
                    <span className="ml-1 text-amber-600">· Calculé sur {n} projets retenus ({nExcluded} exclu{nExcluded > 1 ? 's' : ''})</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100 overflow-visible rounded-b-2xl">
            <KpiCard
              label="Montant total analysé"
              value={fmtK(totalAnalyse)}
              sub={`${n} projet${n !== 1 ? 's' : ''} retenus`}
              color="gray"
            />
            <KpiCard
              label="Écart max observé"
              value={fmtK(ecartMaxEuros)}
              sub={`${fmt(lotDist.min)} → ${fmt(lotDist.max)} €/m²`}
              color="red"
              tooltip={`Différence entre le prix le plus bas et le plus haut observés sur ce lot.\nCalculé pour une surface de ${fmt(avgSurface)} m².\n\nMin : ${fmt(lotDist.min)} €/m²\nMax : ${fmt(lotDist.max)} €/m²\nÉcart : ${fmt(lotDist.max - lotDist.min)} €/m²`}
            />
            <KpiCard
              label="Économie médiane possible"
              value={fmtK(economieEuros)}
              sub={`↓ ${negoPct}% en allant au 1er quartile`}
              color="green"
              tooltip={`Potentiel théorique si ce lot entier était négocié au 1er quartile (P25).\n\nMédiane : ${fmt(lotDist.median)} €/m²\n1er quartile : ${fmt(lotDist.p25)} €/m²\nÉcart : ${fmt(lotDist.median - lotDist.p25)} €/m² × ${fmt(avgSurface)} m²\n\n25% des projets ont obtenu ce prix ou moins.\n\n⚠ Potentiel théorique global — l'économie atteignable (callout vert) applique un facteur de 40% pour tenir compte des contraintes réelles de négociation.`}
            />
            <KpiCard
              label="Médiane de référence"
              value={`${fmt(lotDist.median)} €/m²`}
              sub={`1er quartile : ${fmt(lotDist.p25)} €/m²`}
              color="violet"
            />
          </div>
        </div>
      )}

      {/* ── Section 2 : Dispersion par poste ──────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <span className="w-2 h-2 rounded-full bg-orange-400" />
          <h3 className="text-sm font-semibold text-gray-700">Dispersion par poste</h3>
          <Tooltip
            text={"La bande colorée couvre 50% des projets (P25→P75).\nLe trait = médiane.\n\n🔴 Alerte si l'écart dépasse 20% de la médiane."}
            position="top"
            width="w-64"
          >
            <span className="ml-1 text-xs text-gray-400 underline underline-offset-2 cursor-help">lire le graphique</span>
          </Tooltip>
        </div>

        <div>
          {childrenArray.length === 0 ? (
            <div className="px-5 py-4">
              {lotDist && <RatioRangeBar dist={lotDist} surface={avgSurface} />}
            </div>
          ) : (
            childrenArray.map(([name, dist]) => (
              <DispersionRow
                key={name}
                name={name}
                dist={dist}
                avgSurface={avgSurface}
                projects={filteredProjects}
                path={[selectedLot, name]}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Section 3 : Postes prioritaires à négocier ────────────────────── */}
      {allAnomalies.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <FireIcon className="h-4 w-4 text-red-500" />
            <h3 className="text-sm font-semibold text-gray-700">Postes prioritaires à négocier</h3>
            <Tooltip
              text={"Tous les postes (ensembles et sous-ensembles) classés par enjeu financier = (P75 − P25) × surface.\n\nMarquez un poste comme traité pour le cacher et voir le suivant — travaillez progressivement du plus impactant au moins impactant."}
              position="top"
              width="w-80"
            >
              <span className="ml-1 text-xs text-gray-400 underline underline-offset-2 cursor-help">comment lire</span>
            </Tooltip>
            {resolvedAnomalies.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-full">
                {resolvedAnomalies.length} traité{resolvedAnomalies.length > 1 ? 's' : ''}
              </span>
            )}
            <span className="ml-auto text-xs text-gray-400">Triés par enjeu €</span>
          </div>

          {/* Postes actifs (top 5 non traités) */}
          <div className="divide-y divide-gray-50">
            {activeAnomalies.map((a, i) => (
              <div key={a.key} className="flex items-start gap-3 px-5 py-3.5 hover:bg-gray-50/50 transition-colors group">
                <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 ${
                  i === 0 ? 'bg-red-100 text-red-600'
                          : i === 1 ? 'bg-orange-100 text-orange-600'
                          : i === 2 ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-gray-100 text-gray-500'
                }`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-800">{a.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {a.parentLabel && (
                      <span className="text-gray-300 mr-1.5">{a.parentLabel} ›</span>
                    )}
                    Écart IQR ±{a.ecartPct}% · {a.nbProjets} projet{a.nbProjets !== 1 ? 's' : ''}
                  </div>
                </div>
                {/* Économie + enjeu */}
                <div className="text-right shrink-0 space-y-0.5">
                  {a.economiePotentielle > 0 && (
                    <Tooltip
                      text={`Économie atteignable sur ce poste si vous négociez au 1er quartile (P25) plutôt qu'à la médiane.\n= (médiane − P25) × ${fmt(avgSurface)} m²`}
                      position="left"
                      width="w-64"
                    >
                      <div className="text-sm font-bold text-green-600 cursor-help">
                        ↓ {fmtK(a.economiePotentielle)}
                      </div>
                    </Tooltip>
                  )}
                  <div className="text-xs text-gray-400">
                    <span className="text-red-500 font-semibold">{fmtK(a.enjeuEuros)}</span> d'enjeu
                  </div>
                </div>
                {/* Bouton marquer comme traité */}
                <Tooltip text="Marquer comme traité — le poste suivant apparaîtra" position="left" width="w-56">
                  <button
                    onClick={() => handleToggleResolved(a.key)}
                    className="shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-green-500"
                  >
                    <CheckCircleIcon className="w-5 h-5" />
                  </button>
                </Tooltip>
              </div>
            ))}

            {activeAnomalies.length === 0 && (
              <div className="px-5 py-6 text-center text-sm text-green-700 font-medium">
                ✓ Tous les postes prioritaires ont été traités
              </div>
            )}
          </div>

          {/* Section postes traités (collapsible) */}
          {resolvedAnomalies.length > 0 && (
            <div className="border-t border-gray-100">
              <button
                onClick={() => setShowResolved(v => !v)}
                className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-gray-50 transition-colors"
              >
                <CheckCircleIcon className="w-3.5 h-3.5 text-green-500" />
                <span className="text-xs text-gray-500 flex-1">
                  {resolvedAnomalies.length} poste{resolvedAnomalies.length > 1 ? 's' : ''} traité{resolvedAnomalies.length > 1 ? 's' : ''}
                </span>
                {showResolved
                  ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400" />
                  : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
                }
              </button>
              {showResolved && (
                <div className="divide-y divide-gray-50 bg-green-50/30">
                  {resolvedAnomalies.map(a => (
                    <div key={a.key} className="flex items-center gap-3 px-5 py-2.5">
                      <CheckCircleIcon className="w-4 h-4 text-green-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-gray-500 line-through">{a.name}</span>
                        {a.parentLabel && (
                          <span className="text-xs text-gray-300 ml-1.5">{a.parentLabel}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleToggleResolved(a.key)}
                        className="text-xs text-gray-400 hover:text-violet-600 transition-colors shrink-0"
                      >
                        Réintégrer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="px-5 py-3 bg-green-50 border-t border-green-100 rounded-b-2xl flex items-center justify-between gap-3">
            <p className="text-xs text-green-700">
              <span className="font-semibold">Stratégie :</span>{' '}
              traitez un poste à la fois du plus impactant au moins impactant.
            </p>
            {totalEconomieCvWeighted > 0 && (
              <span className="text-sm font-bold text-green-700 shrink-0">
                {fmtK(totalEconomieCvWeighted)} à aller chercher
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Section 4 : Classement des projets ────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <button
            onClick={() => setShowRankings(v => !v)}
            className="flex items-center gap-2 flex-1 text-left min-w-0"
          >
            <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
            <h3 className="text-sm font-semibold text-gray-700">Classement des projets comparables</h3>
            {nExcluded > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full shrink-0">
                {nExcluded} exclu{nExcluded > 1 ? 's' : ''}
              </span>
            )}
          </button>
          <Tooltip
            text={"Triés du plus grand écart au plus petit.\n\nClic droit sur une ligne → exclure / réintégrer un projet atypique (conjoncture, défi technique spécifique…).\nLes calculs se mettent à jour automatiquement sur les projets retenus.\n\n🟢 vert = sous la médiane\n🔴 rouge = > 15% au-dessus"}
            position="top"
            width="w-80"
          >
            <span className="text-xs text-gray-400 underline underline-offset-2 cursor-help shrink-0">comprendre</span>
          </Tooltip>
          {nExcluded > 0 && (
            <button
              onClick={() => setExcludedProjectIds(new Set())}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors shrink-0 ml-1"
            >
              Réinitialiser
            </button>
          )}
          <button onClick={() => setShowRankings(v => !v)} className="shrink-0 ml-1">
            {showRankings
              ? <ChevronUpIcon className="w-4 h-4 text-gray-400" />
              : <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            }
          </button>
        </div>

        {showRankings && (
          <>
            <div className="px-5 pt-2 pb-1">
              <p className="text-xs text-gray-300">Clic droit sur une ligne pour exclure / réintégrer un projet</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500">
                    <th className="text-left px-5 py-2.5 font-medium">Projet</th>
                    <th className="text-right px-4 py-2.5 font-medium">Ratio €/m²</th>
                    <th className="text-right px-4 py-2.5 font-medium">Écart vs médiane</th>
                    <th className="text-right px-5 py-2.5 font-medium">Coût lot</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {projectRankings.map((r, i) => {
                    const isAbove = r.deviancePct > 15;
                    const isBelow = r.deviancePct < -5;
                    return (
                      <tr
                        key={r.id}
                        onContextMenu={e => { e.preventDefault(); handleToggleProject(r.id); }}
                        className={`cursor-context-menu transition-colors select-none ${
                          r.excluded
                            ? 'bg-gray-50 opacity-40 hover:opacity-60'
                            : i % 2 === 0 ? 'bg-white hover:bg-blue-50/40' : 'bg-gray-50/50 hover:bg-blue-50/40'
                        }`}
                        title={r.excluded ? 'Clic droit pour réintégrer ce projet' : 'Clic droit pour exclure ce projet'}
                      >
                        <td className="px-5 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 tabular-nums w-5 shrink-0">{i + 1}.</span>
                            <span className={`font-medium text-gray-700 truncate max-w-[180px] ${r.excluded ? 'line-through' : ''}`}>
                              {r.projet}
                            </span>
                            {r.excluded && (
                              <span className="text-xs text-amber-500 shrink-0">exclu</span>
                            )}
                          </div>
                        </td>
                        <td className="text-right px-4 py-2.5 font-semibold text-gray-700 tabular-nums">
                          {fmt(r.ratioM2)}
                        </td>
                        <td className="text-right px-4 py-2.5">
                          {r.excluded ? (
                            <span className="text-gray-300 tabular-nums">
                              {r.deviancePct > 0 ? '+' : ''}{r.deviancePct}%
                            </span>
                          ) : (
                            <span className={`inline-block px-2 py-0.5 rounded-full font-semibold tabular-nums ${
                              isAbove ? 'bg-red-50 text-red-600'
                                      : isBelow ? 'bg-green-50 text-green-600'
                                      : 'text-gray-500'
                            }`}>
                              {r.deviancePct > 0 ? '+' : ''}{r.deviancePct}%
                            </span>
                          )}
                        </td>
                        <td className="text-right px-5 py-2.5 text-gray-500 tabular-nums">
                          {fmtK(r.cout)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default AuditLot;
